import {Trans} from 'react-i18next'
import {useParams} from 'react-router-dom'
import messages from '../../../../../assets/messages'
import Loading from '../../../../Common/Loading'
import {PammNetworkTree} from '../../../../PammNetworkTree'
import {usePammNetworkOffers} from '../../../../PammNetworkTree/usePammNetworkOffers'
import {EmptyState} from '../../../../PammOffersTree/PammOffersTree.styled'
import {AUTO_CREATED_OFFER_NAME} from '../../../../PammOffersTree/utils/offerDisplayName'
import {Container} from './components/Container'
import {InvestSelfBanner} from './components/InvestSelfBanner'


/**
 * Money Manager view of their PAMM offers (reached via a PAMM account tile -> "View Offers").
 *
 * Renders the manager's offer together with every nested agent / sub-agent offer as a
 * hierarchical, expandable tree (PT-5399). The page opens on the master view; a manager who also
 * holds an agent position elsewhere can switch to that scoped perspective from the toggle (PT-5717).
 */
export const PammManagerOffers = () => {
  const {accountId} = useParams<{accountId: string}>()
  const managerAccountId = Number(accountId)
  const {agentOffers, deniedTree, error, loading, masterOffers} = usePammNetworkOffers(managerAccountId)

  // This route takes the account id from the URL, so it can be pointed at an account whose Money
  // Manager the viewer does not own. The server is what refuses the tree (PT-5720) — this branch
  // only decides what the refusal looks like, and exists so that path reads as an answer rather
  // than as the blank area a bare `return null` would leave behind. Checked ahead of `error` so a
  // refusal reads as one whether or not the hook also reports an error alongside it.
  if (deniedTree) {
    return (
      <Container>
        <EmptyState data-testid="pamm-manager-offers-denied">
          <Trans {...messages.pammManagerTreeNoAccess} />
        </EmptyState>
      </Container>
    )
  }

  if (error) return null
  if (loading) return <Loading />

  // The auto-created "Default" offer is internal, but it is also the manager's own root: it is the
  // chain-less offer every agent chain of length 1 hangs off. Dropping it before the tree was built
  // handed the whole agent level to whichever other chain-less offer happened to have the lowest id,
  // so an agent who brought nobody appeared to own the manager's network (PT-5875). Keep it as the
  // structural root — the card is titled with the manager's name, not "Default" — and only hide it
  // when the manager has nothing else to show, where it would be the sole card on the page.
  const visibleOffers =
    masterOffers.length > 1 ? masterOffers : masterOffers.filter((offer) => offer.name !== AUTO_CREATED_OFFER_NAME)

  return (
    <Container>
      <InvestSelfBanner pammManagerAccountId={accountId} />
      <PammNetworkTree masterOffers={visibleOffers} agentOffers={agentOffers} defaultView="master" />
    </Container>
  )
}
